import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BooleanConversation, Conversation } from 'src/services/useChat';

export type TChatMode = null | 'chatNow' | 'booleanQuery';

export interface IBooleanQuery {
  phrasesList: string[][];
  excludePhrases: string[];
}

interface ChatState {
  isPictureInPictureMode: boolean;
  pipPosition: { x: number; y: number };
  pipSize: { width: number; height: number };
  chatInput: string;
  conversation: Conversation[];
  booleanConversation: BooleanConversation[];
  chatMode: TChatMode;
  copiedBooleanQuery: IBooleanQuery;
}

export const pipSize = { width: 400, height: window.innerHeight - 200 };
export const pipPositionMargin = 20;

export const initialState: ChatState = {
  isPictureInPictureMode: false,
  chatInput: '',
  conversation: [],
  pipPosition: {
    x: window.innerWidth - (pipSize.width + pipPositionMargin),
    y: window.innerHeight - (pipSize.height + pipPositionMargin + 80)
  },
  pipSize,
  booleanConversation: [],
  chatMode: null,
  copiedBooleanQuery: {
    phrasesList: [[]],
    excludePhrases: []
  }
};

export const initChatNow = {
  id: 'chatNow',
  question: 'Chat now',
  answer:
    'Certainly! Please type the question or keyword you’d like me to summarize or select an option below.'
};

export const initBooleanQuery = {
  id: 'generateBooleanQuery',
  question: 'Generate boolean query',
  answer:
    'Certainly! Please type the question or keyword you’d like me to summarize with boolean creator or select an option below.'
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    togglePictureInPictureMode: (state: ChatState) => {
      state.isPictureInPictureMode = !state.isPictureInPictureMode;
    },
    setPictureInPictureMode: (
      state: ChatState,
      action: PayloadAction<boolean>
    ) => {
      state.isPictureInPictureMode = action.payload;
    },
    setPipPosition: (
      state: ChatState,
      action: PayloadAction<{ x: number; y: number }>
    ) => {
      state.pipPosition = action.payload;
    },
    setPipSize: (
      state: ChatState,
      action: PayloadAction<{ width: number; height: number }>
    ) => {
      state.pipSize = action.payload;
    },
    setChatInput: (state: ChatState, action: PayloadAction<string>) => {
      state.chatInput = action.payload;
    },
    setConversation: (
      state: ChatState,
      action: PayloadAction<Conversation[]>
    ) => {
      state.conversation = action.payload;
    },
    updateLastMessageAnswer: (
      state,
      action: PayloadAction<{ answer: string; createdAt: string }>
    ) => {
      if (state.conversation.length > 0) {
        const lastMessage = state.conversation[state.conversation.length - 1];
        lastMessage.answer = action.payload.answer;
        lastMessage.createdAt = action.payload.createdAt;
      }
    },
    setBooleanConversation: (
      state: ChatState,
      action: PayloadAction<BooleanConversation[]>
    ) => {
      state.booleanConversation = action.payload;
    },
    setChatMode: (state: ChatState, action: PayloadAction<TChatMode>) => {
      state.chatMode = action.payload;
    },
    updateLastBooleanMessageAnswer: (
      state,
      action: PayloadAction<BooleanConversation>
    ) => {
      if (state.booleanConversation.length > 0) {
        const lastMessage =
          state.booleanConversation[state.booleanConversation.length - 1];
        lastMessage.response = action.payload.response;
        lastMessage.createdAt = action.payload.createdAt;
        lastMessage.include = action.payload.include;
        lastMessage.exclude = action.payload.exclude;
      }
    },
    updateCopiedBooleanQuery: (state, action: PayloadAction<IBooleanQuery>) => {
      state.copiedBooleanQuery.phrasesList = action.payload.phrasesList;
      state.copiedBooleanQuery.excludePhrases = action.payload.excludePhrases;
    }
  }
});

export const {
  togglePictureInPictureMode,
  setPictureInPictureMode,
  setPipPosition,
  setPipSize,
  setChatInput,
  setConversation,
  updateLastMessageAnswer,
  setBooleanConversation,
  updateLastBooleanMessageAnswer,
  setChatMode,
  updateCopiedBooleanQuery
} = chatSlice.actions;

export default chatSlice.reducer;
